import React, { useState, useEffect } from 'react';
import { adminAuthAPI } from '../../api/adminAuth';
import { subscriptionAPI } from '../../api/subscriptions';

const UserDetailsModal = ({ user, onClose, onRefresh }) => {
  const [userDetails, setUserDetails] = useState(null);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('active');
  const [trialDays, setTrialDays] = useState(14);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      loadUserDetails();
      loadPlans();
    }
  }, [user]);

  const loadUserDetails = async () => {
    try {
      const res = await adminAuthAPI.getUserDetails(user.id);
      setUserDetails(res.data.data);
      if (res.data.data.plan_type) {
        setSelectedPlan(res.data.data.plan_type);
      }
      if (res.data.data.subscription_status) {
        setSelectedStatus(res.data.data.subscription_status);
      }
    } catch (err) {
      console.error('Failed to load user details:', err);
    }
  };

  const loadPlans = async () => {
    try {
      const res = await subscriptionAPI.getPlans();
      setPlans(res.data.data);
    } catch (err) {
      console.error('Failed to load plans:', err);
    }
  };

  const handleSetSubscription = async () => {
    if (!userDetails?.business_id || !selectedPlan) return;

    setLoading(true);
    try {
      await adminAuthAPI.setSubscription(
        userDetails.business_id,
        selectedPlan,
        selectedStatus,
        selectedStatus === 'trial' ? trialDays : null
      );
      alert('Subscription updated successfully!');
      loadUserDetails();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Failed to set subscription:', err);
      alert('Failed to update subscription');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-800">User Details</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            &times;
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* User Info */}
          <div>
            <h3 className="text-lg font-semibold text-gray-700 mb-3">User Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p className="font-medium">{userDetails?.first_name} {userDetails?.last_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium">{userDetails?.email}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Phone</p>
                <p className="font-medium">{userDetails?.phone || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Role</p>
                <p className="font-medium capitalize">{userDetails?.role || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">User Type</p>
                <p className="font-medium capitalize">{userDetails?.user_type || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <p className={`font-medium ${userDetails?.is_active ? 'text-green-600' : 'text-red-600'}`}>
                  {userDetails?.is_active ? 'Active' : 'Inactive'}
                </p>
              </div>
            </div>
          </div>

          {/* Business Info */}
          {userDetails?.business_id && (
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-3">Business Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Business Name</p>
                  <p className="font-medium">{userDetails?.business_name || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Business Type</p>
                  <p className="font-medium capitalize">{userDetails?.business_type || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Business Email</p>
                  <p className="font-medium">{userDetails?.business_email || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Business Phone</p>
                  <p className="font-medium">{userDetails?.business_phone || '-'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-gray-500">Address</p>
                  <p className="font-medium">{userDetails?.business_address || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Business Status</p>
                  <p className={`font-medium ${userDetails?.business_active ? 'text-green-600' : 'text-red-600'}`}>
                    {userDetails?.business_active ? 'Active' : 'Inactive'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Subscription Info */}
          {userDetails?.business_id && (
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-3">Subscription Management</h3>
              
              {/* Current Subscription */}
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p className="text-sm text-gray-500">Current Plan</p>
                <p className="font-medium text-lg capitalize">{userDetails?.plan_type || 'Free'}</p>
                <p className="text-sm text-gray-500 mt-1">
                  Status: <span className="font-medium capitalize">{userDetails?.subscription_status || 'Inactive'}</span>
                </p>
                {userDetails?.trial_ends_at && (
                  <p className="text-sm text-gray-500">
                    Trial Ends: {new Date(userDetails.trial_ends_at).toLocaleDateString()}
                  </p>
                )}
              </div>

              {/* Set Subscription */}
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Select Plan</label>
                  <select
                    value={selectedPlan || ''}
                    onChange={(e) => setSelectedPlan(e.target.value)}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select a plan</option>
                    {plans.map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.name} - ${plan.price}/month
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="active">Active</option>
                    <option value="trial">Trial</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                {selectedStatus === 'trial' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Trial Days</label>
                    <input
                      type="number"
                      value={trialDays}
                      onChange={(e) => setTrialDays(Number(e.target.value))}
                      className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      min="1"
                      max="365"
                    />
                  </div>
                )}

                <button
                  onClick={handleSetSubscription}
                  disabled={loading || !selectedPlan}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Updating...' : 'Set Subscription'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDetailsModal;
